import globalAgent from 'src/agent';

export type ManagerAccessState = 'locked' | 'pending' | 'rejected' | 'granted';

export type ManagerAccess = {
    state: ManagerAccessState;
    manager_cap: number | null;
    manager_count: number;
    can_create?: boolean;
    profile_ids?: number[];
    profile_id: number | null;
    project_id: number | null;
    request_id: string | null;
    requested_at: string | null;
    reviewed_at: string | null;
    next_step: string;
    workspace: {
        mode: 'full';
        launch: LaunchReadiness;
        telegram_accounts?: Array<{
            id: number;
            label: string;
            ready: boolean;
        }>;
    } | null;
};

export type LaunchReadiness = {
    can_start: boolean;
    blockers: string[];
    credits_available: number;
    can_start_new_dialog: boolean;
    can_continue_charged_dialogs: boolean;
    transport: { ready: boolean; code: string };
};

export type ManagerIntakeSourceMode = 'packages' | 'subscription' | 'both';
export type ManagerLeadSelection = 'target_only' | 'all';
export type ManagerGoalTemplate = 'handoff' | 'consultation' | 'application' | 'questions' | 'custom';

export type ManagerIntakeOptions = {
    source_mode: ManagerIntakeSourceMode;
    lead_selection: ManagerLeadSelection;
    selected: {
        lead_stream_ids: number[];
        subscription_project_ids: number[];
    };
    package_streams: Array<{
        id: number;
        name: string;
        display_name: string;
        status: 'pending' | 'active' | 'paused' | 'ended';
        status_label: string;
        balance: number;
        is_active: boolean;
    }>;
    subscription_projects: Array<{
        id: number;
        name: string;
        is_active: boolean;
        subscription_active: boolean;
        subscription_end_date: string | null;
        ai_analysis_enabled: boolean;
        target_only_available: boolean;
    }>;
    capabilities: {
        packages_available: boolean;
        subscription_available: boolean;
        target_only_ai_classification_available: boolean;
    };
    hints: string[];
};

export type ManagerProfile = {
    id: number;
    name: string;
    description: string;
    status: string;
    status_label: string;
    is_active: boolean;
    goal?: string;
    goal_template?: ManagerGoalTemplate;
    goal_description?: string;
    flow_steps?: string[];
    opening_message?: string;
    handoff_rule?: string;
    success_criteria?: string;
    rejection_criteria?: string;
    next_step?: string;
    readiness?: number;
    telegram_status?: string;
    telegram_client?: number | null;
    launch_readiness?: LaunchReadiness | null;
    auto_send_private_default?: boolean;
    working_timezone?: string;
    working_days?: string[];
    working_hours_start?: string;
    working_hours_end?: string;
    reply_delay_min_seconds?: number;
    reply_delay_max_seconds?: number;
    max_new_conversations_per_day?: number;
    max_outbound_messages_per_hour?: number;
    no_reply_followup_enabled?: boolean;
    no_reply_followup_delay_hours?: number;
    no_reply_followup_text?: string;
    stop_phrases?: string[];
    data_to_collect?: string[];
    max_messages_per_dialog?: number;
    source_mode?: ManagerIntakeSourceMode;
    lead_selection?: ManagerLeadSelection;
    subscription_project_ids?: number[];
    lead_stream_ids?: number[];
    intake_options?: ManagerIntakeOptions;
};

export type BillingSummary = {
    billing_account_scope: string;
    free_dialog_credits_total: number;
    free_dialog_credits_used: number;
    free_dialog_credits_available: number;
    package_dialog_credits: number;
    total_dialog_credits_available: number;
    charged_dialogs_total: number;
    wallet_balance_rub?: string;
    recent_purchases?: DialogPackagePurchase[];
};

export type DialogPackage = {
    id: number;
    title: string;
    credited_dialogs: number;
    total_price: string;
    effective_price_per_dialog: string;
    is_active: boolean;
    is_recommended?: boolean;
};

export type DialogPackagePurchase = {
    id: number;
    package_id: number;
    credited_dialogs: number;
    total_price: string;
    effective_price_per_dialog: string;
    status: 'pending' | 'paid' | 'canceled';
    request_key?: string;
    paid_at?: string | null;
    created_at?: string;
};

export type DialogPackagePurchaseResponse = {
    purchase: DialogPackagePurchase;
    paid_immediately: boolean;
    confirmation_url: string;
    payment_id?: string;
    request_key: string;
    balance?: BillingSummary;
};

export type StoredDialogPackageCheckout = {
    requestKey: string;
    packageId: number;
    purchaseId?: number;
    confirmationUrl?: string;
    status: 'draft' | 'pending' | 'paid' | 'canceled';
};

const checkoutStorageKey = (_product: 'tghunter' | 'leadsmania', accountScope: string) =>
    `ai_manager_dialog_checkout:${accountScope}`;

function isRequestKey(value: unknown): value is string {
    return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(value);
}

export function readDialogPackageCheckout(
    product: 'tghunter' | 'leadsmania',
    accountScope: string
): StoredDialogPackageCheckout | null {
    if (!accountScope) return null;
    try {
        const saved = JSON.parse(localStorage.getItem(checkoutStorageKey(product, accountScope)) || 'null');
        if (
            !saved ||
            !isRequestKey(saved.requestKey) ||
            !Number.isInteger(saved.packageId) ||
            saved.packageId <= 0 ||
            !['draft', 'pending', 'paid', 'canceled'].includes(saved.status)
        ) {
            return null;
        }
        if (saved.purchaseId !== undefined && (!Number.isInteger(saved.purchaseId) || saved.purchaseId <= 0)) {
            return null;
        }
        return saved;
    } catch {
        return null;
    }
}

export function dialogPackageRequestKey(
    product: 'tghunter' | 'leadsmania',
    accountScope: string,
    packageId: number
) {
    if (!accountScope) throw new Error('Не удалось определить платёжный аккаунт.');
    const saved = readDialogPackageCheckout(product, accountScope);
    if (saved?.packageId === packageId && ['draft', 'pending'].includes(saved.status)) {
        return saved.requestKey;
    }
    if (!globalThis.crypto?.randomUUID) {
        throw new Error('Не удалось безопасно начать оплату. Обновите браузер.');
    }
    const requestKey = globalThis.crypto.randomUUID();
    const checkout: StoredDialogPackageCheckout = {
        requestKey,
        packageId,
        status: 'draft',
    };
    try {
        localStorage.setItem(checkoutStorageKey(product, accountScope), JSON.stringify(checkout));
    } catch {
        throw new Error('Не удалось сохранить операцию оплаты. Проверьте доступ к хранилищу браузера.');
    }
    return requestKey;
}

export function rememberDialogPackageCheckout(
    product: 'tghunter' | 'leadsmania',
    accountScope: string,
    checkout: StoredDialogPackageCheckout
) {
    if (!accountScope) throw new Error('Не удалось определить платёжный аккаунт.');
    localStorage.setItem(checkoutStorageKey(product, accountScope), JSON.stringify(checkout));
}

export function clearDialogPackageCheckout(product: 'tghunter' | 'leadsmania', accountScope: string) {
    if (!accountScope) return;
    try {
        localStorage.removeItem(checkoutStorageKey(product, accountScope));
    } catch {
        // A terminal server result remains authoritative if storage is unavailable.
    }
}

export function trustedPaymentUrl(value: unknown) {
    if (typeof value !== 'string') return '';
    try {
        const url = new URL(value);
        const trustedHost = ['yoomoney.ru', 'yookassa.ru'].some(
            (host) => url.hostname === host || url.hostname.endsWith(`.${host}`)
        );
        if (
            url.protocol !== 'https:' ||
            url.username ||
            url.password ||
            (url.port && url.port !== '443') ||
            !trustedHost
        ) {
            return '';
        }
        return url.href;
    } catch {
        return '';
    }
}


export type KnowledgeDocument = {
    id: number;
    title: string;
    body: string;
    document_type: string;
    is_active: boolean;
};

export type ManagerAttachment = {
    id: number;
    title: string;
    description: string;
    file_url: string;
    uploaded_file_url?: string;
    delivery_url?: string;
    caption: string;
    trigger_event: string;
    is_active: boolean;
};

export type ManagerConversation = {
    id: number;
    status: string;
    sales_stage: string;
    sales_stage_title?: string;
    source_message_snapshot: string;
    source_chat_title_snapshot?: string;
    source_author_snapshot?: string;
    latest_message_preview?: string;
    latest_outbound_status?: string | null;
    message_count?: number;
    outbound_count?: number;
    review_priority?: number;
    operator_override?: { active?: boolean } | null;
    dialog_charge?: {
        charged_at?: string;
        ledger_entry_id?: number | null;
    } | null;
};

export type ConversationTimelineItem = {
    id: string;
    kind: string;
    role: string;
    content: string;
    status?: string | null;
    created_at: string;
    metadata?: Record<string, unknown>;
};

export type ManagerOutbound = {
    id: number;
    body: string;
    status: string;
};

export type TestMessage = {
    id: string;
    role: 'user' | 'assistant';
    content: string;
};

export type TestDialogResult = {
    assistantMessage: TestMessage;
    sessionId: number | null;
    sessionState: Record<string, unknown>;
    limits: TestDialogLimits;
};

export type TestDialogLimits = {
    session: { limit: number; used: number; remaining: number };
    daily: {
        limit: number;
        used: number;
        remaining: number;
        timezone: string;
        resets_at: string;
    };
};

type TelegramAuthSession = {
    session_token: string;
    status: 'code_requested' | 'password_required' | 'completed' | 'failed' | 'expired';
    last_error?: string;
};

function unwrapList<T>(value: T[] | { results?: T[] }): T[] {
    if (Array.isArray(value)) return value;
    return Array.isArray(value.results) ? value.results : [];
}

function nextProfilePage(value: unknown) {
    if (typeof value !== 'string' || !value) return null;
    if (value.startsWith('?')) return `/agent/profiles/${value}`;
    try {
        const parsed = new URL(value, 'https://local.invalid');
        const marker = '/agent/profiles/';
        const index = parsed.pathname.indexOf(marker);
        return index >= 0 ? `${parsed.pathname.slice(index)}${parsed.search}` : null;
    } catch {
        return null;
    }
}

class AIManagerService {
    getAccess = async (profileId?: number | null) => {
        const { data } = await globalAgent.get<ManagerAccess>('/agent/tghunter-access/', {
            params: profileId ? { profile_id: profileId } : undefined,
        });
        return data;
    };

    requestAccess = async () => {
        const { data } = await globalAgent.post<ManagerAccess>('/agent/tghunter-access/');
        return data;
    };

    listProfiles = async () => {
        const profiles: ManagerProfile[] = [];
        const visited = new Set<string>();
        let url: string | null = '/agent/profiles/';
        while (url && !visited.has(url)) {
            visited.add(url);
            const { data } = await globalAgent.get<
                ManagerProfile[] | { results?: ManagerProfile[]; next?: string | null }
            >(url);
            if (Array.isArray(data)) return [...profiles, ...data];
            profiles.push(...unwrapList<ManagerProfile>(data));
            url = nextProfilePage(data.next);
        }
        return profiles;
    };

    createProfile = async (values: Pick<ManagerProfile, 'name'>, idempotencyKey: string) => {
        const { data } = await globalAgent.post<ManagerProfile>('/agent/profiles/', values, {
            headers: { 'Idempotency-Key': idempotencyKey },
        });
        return data;
    };

    updateProfile = async (profileId: number, values: Partial<ManagerProfile>) => {
        const { data } = await globalAgent.patch<ManagerProfile>(`/agent/profiles/${profileId}/`, values);
        return data;
    };

    getBilling = async () => {
        const { data } = await globalAgent.get<BillingSummary>('/agent/billing/summary/');
        return data;
    };

    listDialogPackages = async () => {
        const { data } = await globalAgent.get<DialogPackage[] | { results?: DialogPackage[] }>(
            '/agent/dialog-packages/'
        );
        return unwrapList(data);
    };

    purchaseDialogPackage = async (packageId: number, requestKey: string) => {
        const { data } = await globalAgent.post<DialogPackagePurchaseResponse>(
            '/agent/billing/dialog-packages/purchase/',
            {
                request_key: requestKey,
                package_id: packageId,
                use_wallet_rub: false,
                use_bonus_points: false,
                promo_code: '',
            }
        );
        return data;
    };

    reconcileDialogPackagePurchase = async (purchaseId: number, requestKey: string) => {
        const { data } = await globalAgent.post<DialogPackagePurchaseResponse>(
            `/agent/billing/dialog-packages/purchases/${purchaseId}/reconcile/`,
            { request_key: requestKey }
        );
        return data;
    };


    listKnowledge = async (profileId: number) => {
        const { data } = await globalAgent.get<KnowledgeDocument[] | { results?: KnowledgeDocument[] }>(
            '/agent/knowledge-documents/',
            { params: { agent_profile: profileId } }
        );
        return unwrapList(data);
    };

    createKnowledge = async (profileId: number, title: string, body: string) => {
        const { data } = await globalAgent.post<KnowledgeDocument>('/agent/knowledge-documents/', {
            agent_profile: profileId,
            title,
            body,
            scope: 'profile',
            document_type: 'script',
            priority: 10,
            tags: [],
            is_active: true,
        });
        return data;
    };

    updateKnowledge = async (documentId: number, title: string, body: string) => {
        const { data } = await globalAgent.patch<KnowledgeDocument>(`/agent/knowledge-documents/${documentId}/`, {
            title,
            body,
        });
        return data;
    };

    deleteKnowledge = async (documentId: number) => {
        await globalAgent.delete(`/agent/knowledge-documents/${documentId}/`);
    };

    listAttachments = async (profileId: number) => {
        const { data } = await globalAgent.get<ManagerAttachment[] | { results?: ManagerAttachment[] }>(
            '/agent/attachments/',
            { params: { agent_profile: profileId } }
        );
        return unwrapList(data);
    };

    createAttachment = async (
        profileId: number,
        values: {
            title: string;
            fileUrl: string;
            file: File | null;
            caption: string;
        }
    ) => {
        const form = new FormData();
        form.append('agent_profile', String(profileId));
        form.append('title', values.title);
        form.append('description', '');
        form.append('file_url', values.fileUrl);
        form.append('caption', values.caption);
        form.append('trigger_event', 'manual');
        form.append('send_once_per_conversation', 'true');
        form.append('is_active', 'true');
        form.append('metadata', '{}');
        if (values.file) form.append('uploaded_file', values.file);
        const { data } = await globalAgent.post<ManagerAttachment>('/agent/attachments/', form);
        return data;
    };

    deleteAttachment = async (attachmentId: number) => {
        await globalAgent.delete(`/agent/attachments/${attachmentId}/`);
    };

    testDialog = async (
        profileId: number,
        messages: TestMessage[],
        sessionId: number | null,
        sessionState: Record<string, unknown> | null
    ): Promise<TestDialogResult> => {
        const { data } = await globalAgent.post(
            `/agent/profiles/${profileId}/test-dialog/`,
            {
                session_id: sessionId,
                messages: messages.map(({ role, content }) => ({ role, content })),
                known_facts: sessionState?.known_facts || {},
                current_stage_key: sessionState?.current_stage_key || null,
                lifecycle_status: sessionState?.lifecycle_status || null,
                sales_stage: sessionState?.sales_stage || null,
                sales_outcome: sessionState?.sales_outcome || null,
            },
            { timeout: 45000 }
        );
        const content = String(data?.assistant_message?.content || data?.reply_draft || '').trim();
        return {
            assistantMessage: {
                id: String(data?.assistant_message?.id || `assistant-${Date.now()}`),
                role: 'assistant',
                content: content || 'Ответ не сформирован.',
            },
            sessionId: Number(data?.session?.id) || null,
            sessionState: data?.session_state || {},
            limits: data?.limits,
        };
    };

    getTestDialogLimits = async (profileId: number, sessionId: number | null = null): Promise<TestDialogLimits> => {
        const { data } = await globalAgent.get(`/agent/profiles/${profileId}/test-dialog/`, {
            params: sessionId ? { session_id: sessionId } : undefined,
        });
        return data.limits;
    };

    acceptSetupTest = async (profileId: number, sessionId: number, rating: number) => {
        await globalAgent.post(`/agent/profiles/${profileId}/setup-test-review/`, {
            session_id: sessionId,
            rating,
        });
    };

    updateStatus = async (profileId: number, status: 'running' | 'paused') => {
        const { data } = await globalAgent.patch<ManagerProfile | { manager: ManagerProfile }>(
            `/agent/profiles/${profileId}/status/`,
            { status }
        );
        return 'manager' in data ? data.manager : data;
    };

    listConversations = async (profileId: number) => {
        const { data } = await globalAgent.get<ManagerConversation[]>(`/agent/profiles/${profileId}/conversations/`, {
            params: { limit: 100 },
        });
        return Array.isArray(data) ? data : [];
    };

    getConversationTimeline = async (profileId: number, conversationId: number) => {
        const { data } = await globalAgent.get<{
            conversation: ManagerConversation;
            timeline: ConversationTimelineItem[];
        }>(`/agent/conversations/${conversationId}/messages/`, {
            params: { agent_profile: profileId },
        });
        if (data.conversation.id !== conversationId) throw new Error('Диалог не принадлежит менеджеру');
        return data;
    };

    handoffConversation = async (profileId: number, conversationId: number, note: string) => {
        const { data } = await globalAgent.post<ManagerConversation>(
            `/agent/conversations/${conversationId}/handoff/`,
            { note },
            { params: { agent_profile: profileId } }
        );
        return data;
    };

    setOperatorOverride = async (profileId: number, conversationId: number, active: boolean) => {
        const { data } = await globalAgent.post<ManagerConversation>(
            `/agent/conversations/${conversationId}/operator-override/`,
            {
                active,
                note: active ? 'Взято сотрудником из кабинета TGHunter' : 'Возвращено AI-менеджеру',
            },
            { params: { agent_profile: profileId } }
        );
        return data;
    };

    moveConversationStage = async (profileId: number, conversationId: number, salesStage: string) => {
        const { data } = await globalAgent.post<ManagerConversation>(
            `/agent/conversations/${conversationId}/move-stage/`,
            { sales_stage: salesStage, note: 'Изменено в кабинете TGHunter' },
            { params: { agent_profile: profileId } }
        );
        return data;
    };

    resolveConversation = async (
        profileId: number,
        conversationId: number,
        toStatus: 'goal_completed' | 'disqualified' | 'stopped' | 'failed'
    ) => {
        const { data } = await globalAgent.post<ManagerConversation>(
            `/agent/conversations/${conversationId}/resolve/`,
            {
                to_status: toStatus,
                reason_code: 'owner_review',
                note: 'Завершено в кабинете TGHunter',
            },
            { params: { agent_profile: profileId } }
        );
        return data;
    };

    listOutbounds = async (profileId: number, conversationId: number) => {
        const { data } = await globalAgent.get<ManagerOutbound[]>(`/agent/conversations/${conversationId}/outbounds/`, {
            params: { agent_profile: profileId },
        });
        return Array.isArray(data) ? data : [];
    };

    approveOutbound = async (outboundId: number, body: string) => {
        const { data } = await globalAgent.patch<ManagerOutbound>(
            `/agent/outbounds/${outboundId}/approve/`,
            { body },
            { headers: { 'Idempotency-Key': `tgh-review-${outboundId}` } }
        );
        return data;
    };

    startTelegramAuth = async (profileId: number, phoneNumber: string) => {
        const { data } = await globalAgent.post<TelegramAuthSession>('/agent/telegram-auth/start/', {
            agent_profile_id: profileId,
            phone_number: phoneNumber,
        });
        return data;
    };

    verifyTelegramAuth = async (sessionToken: string, code: string, password: string) => {
        const { data } = await globalAgent.post<TelegramAuthSession>('/agent/telegram-auth/verify/', {
            session_token: sessionToken,
            code,
            password,
        });
        return data;
    };
}

export default new AIManagerService();
